import { css } from '@emotion/react';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import t from 'react-translate';
import { AngularContext } from 'react-app';

import {
  createCustomDataReport,
  deleteCustomDataReport,
  generateCustomDataReport,
  getCustomDataReports,
} from 'redux/actions/custom-downloads';
import { fetchJourney } from 'redux/actions/learning-journeys';
import { getCurrentJourney } from 'redux/selectors/learning-journeys';
import { DataDownloadInfo, DataDownloadsResponse } from 'redux/schemas/api/custom-downloads';
import { useAppDispatch } from 'redux/store';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { AlertMessageType } from 'redux/schemas/app/alert-message';

import { gray6 } from 'styles/global_defaults/colors';
import { config } from '@config/pendo.config.json';

import NvIcon from 'shared/components/nv-icon';
import LoadingWrapper, { LoaderType } from 'shared/components/loading-wrapper';
import useCustomReportPusherEvents from 'custom_downloads/hooks/use-custom-report-pusher-events';
import useShowAllToggle from 'shared/hooks/use-show-all-toggle';
import DataDownloadsCard from 'custom_downloads/components/data-downloads-card';
import DownloadsCardOptions from 'custom_downloads/components/downloads-card-options';
import CreateDownloadModal from 'custom_downloads/components/create-download-modal';
import DataDownloadsListModal from 'custom_downloads/components/data-downloads-list-modal';

const styles = css`
  .empty-state {
    border: 1px dashed ${gray6};
  }
`;

const DEFAULT_MIN = 6;

const DataDownloads = () => {
  const dispatch = useAppDispatch();
  const currentJourney = useSelector(getCurrentJourney);
  const { injectServices } = useContext(AngularContext);
  const [$state] = injectServices(['$state']);
  const { catalogId } = $state.params;

  const [customData, setCustomData] = useState<Pick<DataDownloadsResponse, 'lastVisitedAt' | 'showSftpToggle'>>();
  const [reports, setReports] = useState<DataDownloadInfo[]>([]);
  const [showCreateDownloadModal, setShowCreateDownloadModal] = useState(false);
  const [showDownloadsListModal, setShowDownloadsListModal] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);
  const [isReportsLoading, setIsReportsLoading] = useState(true);

  const {
    showAll: showAllReports,
    visibleItems: visibleReports,
    hasMore: hasMoreReports,
    toggleShowAll: toggleShowAllReports,
    resetShowAll: resetShowAllReports,
  } = useShowAllToggle(reports, DEFAULT_MIN);

  const selectedReport = useMemo(
    () => reports.find(({ reportId }) => reportId === selectedReportId) ?? null,
    [reports, selectedReportId],
  );

  const loadReports = useCallback(async (showLoading = true) => {
    if (showLoading) setIsReportsLoading(true);
    try {
      const result: DataDownloadsResponse = await dispatch(getCustomDataReports({ catalogId })).unwrap();
      setReports(result.dataDownload ?? []);
      setCustomData({
        lastVisitedAt: result.lastVisitedAt,
        showSftpToggle: result.showSftpToggle,
      });
    } catch {
      // Keep the last-good reports and customData so a transient refresh
      // failure doesn't blank out the UI.
    } finally {
      if (showLoading) setIsReportsLoading(false);
    }
  }, [catalogId, dispatch]);

  useEffect(() => {
    if (!currentJourney) {
      dispatch(fetchJourney({ catalogId, shouldSetCourse: true }));
    }
  }, [catalogId, currentJourney, dispatch]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  useCustomReportPusherEvents(currentJourney?.id, (data) => {
    if (data.courseId !== currentJourney?.id) return;
    if (data.reportType && data.reportType !== 'download') return;
    loadReports(false);
  });

  const handleCreateDownload = async (name: string) => {
    await dispatch(createCustomDataReport({
      catalogId,
      title: name || t.LEARNING_JOURNEYS.DATA_DOWNLOADS.CREATE_NEW_DOWNLOAD_MODAL.DEFAULT(),
    })).unwrap();
    await loadReports(false);
    setShowCreateDownloadModal(false);
    dispatch(addAlertMessage({
      type: AlertMessageType.SUCCESS,
      header: t.CUSTOM_DOWNLOADS.CREATE_REPORT.DATA_DOWNLOAD_SUCCESS_TITLE(),
      message: t.CUSTOM_DOWNLOADS.CREATE_REPORT.SUCCESS_MESSAGE(),
      duration: 20000,
    }));
  };

  const handleGenerateReport = (report: DataDownloadInfo) => {
    dispatch(generateCustomDataReport({ catalogId, reportId: report.reportId }))
      .unwrap()
      .then(() => loadReports(false))
      // failure alert is already dispatched inside generateCustomDataReport; this just
      // prevents an unhandled rejection from propagating.
      .catch(() => {});
  };

  const handleDeleteReport = (report: DataDownloadInfo) => {
    dispatch(openConfirmationDialog({
      title: t.CUSTOM_DOWNLOADS.DELETE_DATA_DOWNLOAD(),
      bodyText: t.CUSTOM_DOWNLOADS.DELETE_DATA_REPORT_CONFIRMATION_MSG(),
      confirmText: t.FORM.YES_SURE(),
      confirmButtonVariant: 'danger',
      onConfirm: () => {
        dispatch(deleteCustomDataReport({ catalogId, reportId: report.reportId }))
          .unwrap()
          .then(() => {
            setReports((prev) => {
              const updated = prev.filter((r) => r.reportId !== report.reportId);
              if (updated.length <= DEFAULT_MIN) {
                resetShowAllReports();
              }
              return updated;
            });
          })
          // failure alert is already dispatched inside deleteCustomDataReport; this just
          // prevents an unhandled rejection from propagating.
          .catch(() => {});
      },
    }));
  };

  const handleOpenDownloadsListModal = (report: DataDownloadInfo) => {
    setSelectedReportId(report.reportId);
    setShowDownloadsListModal(true);
  };

  const handleReportUpdated = (reportId: number, patch: Partial<DataDownloadInfo>) => {
    setReports(prev => prev.map(report => (
      report.reportId === reportId ? { ...report, ...patch } : report
    )));
  };

  return (
    <div css={styles} className='d-flex flex-column text-center container'>
      <div>
        <h2 className='page-subtitle'>
          {t.LEARNING_JOURNEYS.DATA_DOWNLOADS.TITLE()}
        </h2>
        <div className='text-regular text-gray-2'>
          {t.LEARNING_JOURNEYS.DATA_DOWNLOADS.DESCRIPTION()}
        </div>
        <button
          type='button'
          className='d-inline-flex btn btn-link text-primary align-items-center mt-1 mb-3'
          onClick={() => setShowCreateDownloadModal(true)}
          data-qa={config.pendo.learningJourneys.dataDownloads.createNew}
        >
          <NvIcon icon='add' size='xss-smallest' className='mr-1' />
          {t.LEARNING_JOURNEYS.DATA_DOWNLOADS.CREATE_NEW()}
        </button>
      </div>

      <LoadingWrapper
        isLoaded={!isReportsLoading}
        loaderType={LoaderType.PLACEHOLDER}
        keepRendered={false}
        className='w-100'
      >
        <>
          {reports.length > 0 && (
            <>
              <div className='row'>
                {visibleReports.map((report) => (
                  <div key={report.reportId} className='col-md-4 col-sm-6 col-xs-6'>
                    <DataDownloadsCard
                      downloadInfo={report}
                      type='dataDownload'
                      lastVisitedAt={customData?.lastVisitedAt ?? undefined}
                      onViewDownloads={() => handleOpenDownloadsListModal(report)}
                      generateReport={() => handleGenerateReport(report)}
                      cardOptions={(
                        <DownloadsCardOptions
                          downloadInfo={report}
                          type='dataDownload'
                          catalogId={catalogId}
                          showSftpToggle={customData?.showSftpToggle ?? false}
                          deleteReport={() => handleDeleteReport(report)}
                          onReportUpdated={handleReportUpdated}
                        />
                      )}
                    />
                  </div>
                ))}
              </div>
              {hasMoreReports && (
                <button
                  type='button'
                  className='btn btn-link text-primary my-4'
                  onClick={toggleShowAllReports}
                  data-qa={config.pendo.learningJourneys.dataDownloads.showAll}
                >
                  {showAllReports ? t.SHARED.SHOW_LESS() : t.SHARED.SHOW_ALL()}
                </button>
              )}
            </>
          )}

          {reports.length === 0 && (
            <div className='empty-state bg-gray-7 border-rounded text-gray-2 p-5'>
              {t.CUSTOM_DOWNLOADS.NO_DOWNLOAD_GENERATED()}
            </div>
          )}
        </>
      </LoadingWrapper>

      <CreateDownloadModal
        show={showCreateDownloadModal}
        onClose={() => setShowCreateDownloadModal(false)}
        onCreate={handleCreateDownload}
      />

      <DataDownloadsListModal
        show={showDownloadsListModal}
        onClose={() => setShowDownloadsListModal(false)}
        title={selectedReport?.reportTitle ?? ''}
        reportId={selectedReportId}
        courseId={currentJourney?.id ?? null}
        reportStatus={selectedReport?.reportStatus}
        isDailySftp={selectedReport?.isDailySftp}
      />
    </div>
  );
};

export default DataDownloads;
